import { VariableKind } from '../constants'
import type { TemplateVariable } from '../types'
import { emptyLoopRow, type LoopRow } from './payloadSkeleton'

/**
 * Plausible example values for a template's variables, inferred from what each one is called.
 *
 * The job this does is let someone see their template rendered with something that looks like a
 * real document, in one click, without typing twenty fields. So the values have to read as real:
 * "Amelia" and "1,240.00" tell you immediately whether a name field is too narrow or a total is
 * misaligned, and "Lorem ipsum" and "xxxx" tell you nothing. Nothing here is random text.
 *
 * Every pool holds several values and one is picked at random per call, so pressing the button
 * again gives a different document — which is the cheapest way to notice that a layout only
 * survives short names, or that a column is sized for two digits.
 */

/** Phone numbers use the 555 exchange, which is reserved and cannot be dialled. */
const FIRST_NAMES = ['Amelia', 'Marcus', 'Priya', 'Tobias', 'Inés', 'Rafael', 'Noor', 'Gideon',
                     'Saoirse', 'Daniel', 'Yusuf', 'Margot', 'Elias', 'Freya', 'Chidi']
const LAST_NAMES  = ['Okafor', 'Lindqvist', 'Marchetti', 'Nakamura', 'Delacroix', 'Thornbury',
                     'Abernathy', 'Vasquez', 'Haraldsen', 'Whitlock', 'Baptiste', 'Carraway']
const COMPANIES   = ['Northwind Logistics', 'Kestrel Analytics', 'Aldergrove Mills',
                     'Beacon & Thorne', 'Meridian Freight', 'Calderwood Partners',
                     'Halcyon Dynamics', 'Pinewood Clearing', 'Ravensmoor Group']
const JOB_TITLES  = ['Operations Manager', 'Staff Engineer', 'Head of Compliance',
                     'Account Director', 'Logistics Coordinator', 'Chief Financial Officer',
                     'Procurement Lead', 'Regional Supervisor']
const STREETS     = ['118 Alder Street', '7 Kestrel Lane', '2400 Meridian Avenue',
                     '64 Thornbury Road', '915 Calderwood Drive', '31 Harbourgate Way',
                     '1206 Pinewood Crescent']
const CITIES      = ['Dunmore', 'Castlebridge', 'Ashford', 'Fairhaven', 'Northgate', 'Riverton',
                     'Glenmoor', 'Westcliff', 'Thornbury']
const REGIONS     = ['California', 'Ontario', 'Queensland', 'Bavaria', 'County Cork', 'Texas',
                     'North Yorkshire', 'Jalisco']
const POSTCODES   = ['04562', '91320', 'K1A 0B1', 'D02 XY45', 'SW1A 2AA', '30301', '98104']
const COUNTRIES   = ['United States', 'Canada', 'Ireland', 'Germany', 'Australia',
                     'United Kingdom', 'Mexico', 'Japan']
const PHONES      = ['(555) 014-2287', '(555) 920-4416', '+1 555 338 7702', '(555) 471-6093',
                     '+44 20 5550 1188']
const DOMAINS     = ['northwind.example', 'kestrel.example', 'aldergrove.example',
                     'meridianfreight.example', 'halcyon.example']
const SENTENCES   = [
  'Quarterly review completed; no exceptions were raised.',
  'Delivered to the Dunmore depot ahead of schedule.',
  'Replaces the agreement dated 14 March and supersedes all prior terms.',
  'Signed on behalf of Northwind Logistics by the Operations Manager.',
  'Includes freight, handling and insurance to the destination port.',
  'Approved by Finance pending receipt of the supporting invoice.',
]
/** Single proper nouns, for a text field whose name says nothing about what belongs in it. */
const PROPER_NOUNS = ['Kestrel', 'Thornbury', 'Aldergrove', 'Meridian', 'Halcyon', 'Calderwood',
                      'Ravensmoor', 'Harbourgate', 'Pinewood', 'Westcliff']
const CURRENCIES  = ['USD', 'EUR', 'GBP', 'CAD', 'AUD']
const PRODUCTS    = ['Pallet jack, 2.5t', 'Insulated shipping crate', 'Thermal label roll',
                     'Loading dock bumper', 'Stretch wrap, 500mm', 'Steel banding coil']

function pick<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)]
}

function int(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1))
}

/** A decimal with thousands separators, the way an amount is normally written on a document. */
function money(min: number, max: number): string {
  const value = min + Math.random() * (max - min)
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function isoDate(dayOffsetMin: number, dayOffsetMax: number): string {
  const d = new Date()
  d.setDate(d.getDate() + int(dayOffsetMin, dayOffsetMax))
  return d.toISOString().slice(0, 10)
}

export type SampleKind =
  | 'firstName' | 'lastName' | 'fullName' | 'email' | 'phone' | 'company' | 'jobTitle'
  | 'street' | 'city' | 'region' | 'postcode' | 'country'
  | 'date' | 'money' | 'percent' | 'count' | 'year' | 'currency'
  | 'identifier' | 'url' | 'sentence' | 'product' | 'number' | 'text'

/**
 * Rules are tried in order, so the specific ones come first: `firstName` has to win before `name`,
 * and `postalCode` before `code`. A key is matched on its last path segment with separators
 * stripped, so `customer.first_name`, `customerFirstName` and `FirstName` all land in one place.
 */
const RULES: ReadonlyArray<readonly [RegExp, SampleKind]> = [
  [/^(first|given|fore)name$|^fname$/, 'firstName'],
  [/^(last|sur|family)name$|^lname$/, 'lastName'],
  // Before the generic name rule: `companyName` and `vendorName` end in "name" but are not people.
  [/company|organisation|organization|employer|vendor|supplier|business|firm/, 'company'],
  [/(full|customer|client|contact|recipient|employee|member|person|signer|author|sender)?name$/, 'fullName'],
  [/email|mailaddress/, 'email'],
  [/phone|mobile|telephone|^tel$|fax/, 'phone'],
  [/jobtitle|position|role|occupation|^title$/, 'jobTitle'],
  [/street|address(line)?[12]?$|^addr/, 'street'],
  [/city|town|locality|suburb/, 'city'],
  [/state|province|region|county/, 'region'],
  [/postcode|postalcode|zipcode|^zip$|^postal$/, 'postcode'],
  [/country|nation/, 'country'],
  [/currencycode|^currency$/, 'currency'],
  [/percent|^rate$|^pct$|discount|taxrate|interest/, 'percent'],
  [/date|^dob$|birthday|issued|expires|expiry|due|deadline|effective|signedon/, 'date'],
  [/amount|price|total|subtotal|cost|fee|salary|balance|payment|charge|value|tax$|^sum$/, 'money'],
  [/^year$|fiscalyear/, 'year'],
  [/quantity|^qty$|count$|^items?$|units|^num$|number of/, 'count'],
  [/invoice|ordernumber|^sku$|reference|^ref$|accountnumber|policynumber|^code$|^id$|identifier/, 'identifier'],
  [/url|website|link|domain|homepage/, 'url'],
  [/description|notes?$|comment|message|summary|remarks|details|body|terms/, 'sentence'],
  [/product|item|^goods$|service|material/, 'product'],
]

/** Names that read as numeric even though nothing more specific matched. */
const NUMERIC_HINT = /(count|total|num|qty|quantity|amount|age|score|level|index|rank|weight|height|width|length|size|days|hours|minutes|years|months|^n$)/

/** The last path segment, lowercased with separators removed: `order.line_total` → `linetotal`. */
export function matchKey(key: string): string {
  const last = key.split('.').pop() ?? key
  return last.replace(/[^A-Za-z0-9]/g, '').toLowerCase()
}

export function inferSampleKind(key: string): SampleKind {
  const k = matchKey(key)
  for (const [pattern, kind] of RULES) {
    if (pattern.test(k)) return kind
  }
  // Nothing matched. A name that reads numerically gets a number; anything else gets a proper
  // noun, because a recognisable word in the output beats filler that tells the user nothing.
  return NUMERIC_HINT.test(k) ? 'number' : 'text'
}

export function sampleForKind(kind: SampleKind): string {
  switch (kind) {
    case 'firstName':  return pick(FIRST_NAMES)
    case 'lastName':   return pick(LAST_NAMES)
    case 'fullName':   return `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`
    case 'email':      return `${pick(FIRST_NAMES).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')}.${pick(LAST_NAMES).toLowerCase()}@${pick(DOMAINS)}`
    case 'phone':      return pick(PHONES)
    case 'company':    return pick(COMPANIES)
    case 'jobTitle':   return pick(JOB_TITLES)
    case 'street':     return pick(STREETS)
    case 'city':       return pick(CITIES)
    case 'region':     return pick(REGIONS)
    case 'postcode':   return pick(POSTCODES)
    case 'country':    return pick(COUNTRIES)
    case 'currency':   return pick(CURRENCIES)
    case 'date':       return isoDate(-120, 180)
    case 'money':      return money(45, 9850)
    case 'percent':    return String(pick([5, 7.5, 10, 12.5, 15, 20, 22]))
    case 'count':      return String(int(1, 48))
    case 'year':       return String(new Date().getFullYear() - int(0, 4))
    case 'identifier': return `${pick(['INV', 'ORD', 'REF', 'ACC', 'SKU'])}-${int(10000, 99999)}`
    case 'url':        return `https://www.${pick(DOMAINS)}`
    case 'sentence':   return pick(SENTENCES)
    case 'product':    return pick(PRODUCTS)
    case 'number':     return String(int(2, 240))
    case 'text':       return pick(PROPER_NOUNS)
  }
}

/** A plausible value for one variable, chosen fresh on every call. */
export function sampleValue(key: string): string {
  return sampleForKind(inferSampleKind(key))
}

export interface SampleFormState {
  scalarValues: Record<string, string>
  conditionalValues: Record<string, boolean>
  loopRows: Record<string, LoopRow[]>
}

/**
 * Example values for every variable in a template, in the shape the payload editor holds.
 *
 * Loops get two or three rows rather than one: a repeating section that has only ever been seen
 * with a single row is the most common way a table's borders or row striping turn out wrong.
 */
export function buildSampleFormState(variables: TemplateVariable[]): SampleFormState {
  const scalarValues: Record<string, string> = {}
  const conditionalValues: Record<string, boolean> = {}
  const loopRows: Record<string, LoopRow[]> = {}

  for (const v of variables) {
    if (v.variableKindLookupId === VariableKind.SCALAR && !v.loopKey) {
      scalarValues[v.normalizedKey] = sampleValue(v.normalizedKey)
    } else if (v.variableKindLookupId === VariableKind.CONDITIONAL) {
      conditionalValues[v.normalizedKey] = Math.random() < 0.5
    }
  }

  for (const v of variables) {
    if (v.variableKindLookupId !== VariableKind.LOOP) continue
    const subFields = variables.filter(sf => sf.loopKey === v.normalizedKey)
    const rowCount  = int(2, 3)
    loopRows[v.normalizedKey] = Array.from({ length: rowCount }, () =>
      subFields.length === 0
        ? emptyLoopRow(subFields)
        : Object.fromEntries(subFields.map(sf => [sf.normalizedKey, sampleValue(sf.normalizedKey)])),
    )
  }

  return { scalarValues, conditionalValues, loopRows }
}
